/**
 * @file Asset service and issue log submit owner.
 */

import {
    doc,
    serverTimestamp,
    updateDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import { state } from "../../app/state/state.js";
import { formItemData } from "../../shared/forms/formModalState.js";
import {
    findItem,
} from "../../shared/utils/itemLookup.js";
import {
    resolveIssueClosedFromStatus,
} from "../../shared/utils/closedState.js";

function cleanCurrency(value) {
    if ((value === null || value === undefined) ||
        typeof value !== "string" ||
        value.trim() === "") {
        return null;
    }
    const number = Number(String(value).replace(/[$,]/g, ""));
    return Number.isNaN(number) || !Number.isFinite(number) ? null : number;
}

/**
 * Saves an asset service or issue log form payload.
 * @param {object} params Submit context.
 * @param {object} params.data Form payload.
 * @param {string} params.id Existing log ID.
 * @param {string} params.parentId Parent asset ID.
 * @param {'asset_service'|'asset_issue'} params.type Form type.
 * @returns {Promise<void>}
 */
export async function saveAssetLogForm({ data, id, parentId, type }) {
    if (!parentId) {
        throw new Error("Parent asset ID is missing for log entry.");
    }

    data.cost = cleanCurrency(data.cost);
    data.managed_by = formItemData.managed_by || [];
    if (type === "asset_issue") {
        data.isClosedResolved = resolveIssueClosedFromStatus(data.status);
    }

    const assetRef = doc(db, "assets", parentId);
    const asset = findItem(parentId);
    if (!asset) {
        throw new Error(`Parent asset ${parentId} not found.`);
    }

    const logType = type === "asset_service" ? "service_log" : "issue_log";
    const newLogs = [...(asset[logType] || [])];
    const timestamp = new Date().toISOString();
    const logAction = id ? "edited" : "created";
    const changelogEntry = {
        userId: state.currentUser?.uid,
        timestamp,
        action: logAction,
    };

    if (id) {
        const logIndex = newLogs.findIndex((log) => log.id === id);
        if (logIndex === -1) {
            throw new Error(`Log ${id} not found in ${logType}.`);
        }
        const originalLog = newLogs[logIndex];
        newLogs[logIndex] = {
            ...originalLog,
            ...data,
            changelog: [...(originalLog.changelog || []), changelogEntry],
        };
    } else {
        newLogs.push({
            id: `log-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
            ...data,
            changelog: [changelogEntry],
        });
    }

    const assetUpdatePayload = {
        [logType]: newLogs,
        updatedBy: state.currentUser.uid,
        updatedAt: serverTimestamp(),
    };

    await updateDoc(assetRef, assetUpdatePayload);
}
